// React Query mutation hooks for email attachment operations

import { useApiClient } from '@commander/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EmailAttachmentCreateData } from '../../api/emailAttachmentApi';
import { createEmailAttachmentApi } from '../../api/emailAttachmentApi';
import { communicationKeys } from '../communicationKeys';

/**
 * Hook to upload an email attachment
 */
export function useUploadEmailAttachment() {
  const apiClient = useApiClient();
  const attachmentApi = createEmailAttachmentApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EmailAttachmentCreateData) => attachmentApi.uploadEmailAttachment(data),
    // Non-idempotent: auto-retry can create duplicate GCS uploads / attachment records
    retry: 0,
    onSuccess: (_, variables) => {
      // If attachment is associated with an email, invalidate that email's data
      if (variables.email_id) {
        queryClient.invalidateQueries({
          queryKey: communicationKeys.emails.thread(variables.email_id),
        });
      }
    },
  });
}

/**
 * Hook to delete an email attachment
 */
export function useDeleteEmailAttachment() {
  const apiClient = useApiClient();
  const attachmentApi = createEmailAttachmentApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => attachmentApi.deleteEmailAttachment(id),
    onSuccess: () => {
      // Invalidate email lists/threads that might contain this attachment
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.all });
    },
  });
}
